import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import {
  Box,
  CircularProgress,
  Button,
  Paper,
  Tab,
  Tabs,
  IconButton,
  Typography,
  Tooltip,
  Chip,
  Stack,
  Avatar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import AbcIcon from '@mui/icons-material/Abc';
import NumbersIcon from '@mui/icons-material/Numbers';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useLocation, useNavigate } from 'react-router-dom';
import useAPICall from '../../hooks/useAPICall';
import { useConfigTabs } from '../../contexts/ConfigTabsContext';
import GlobalMaterialTable from './GlobalMaterialTable';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';

const toolbarButtonSx = {
  px: 3,
  py: 0.75,
  
  color:"white",
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '8px',
  backgroundColor: '#1E88E5',
  boxShadow: '0 2px 6px rgba(30, 136, 229, 0.25)',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '& .MuiButton-startIcon': { mr: 1 },
  '&:hover': {
    backgroundColor: '#1565C0',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(30, 136, 229, 0.35)',
  },
  '&:active': { transform: 'translateY(0)' }
};

const DEFAULT_CONFIG_COLUMN_WIDTH = 160;

const configTabsSx = {
  minHeight: 48,
  '& .MuiTabs-flexContainer': {
    gap: { xs: 1, sm: 2 },
  },
  '& .MuiTab-root': {
    minHeight: 48,
    minWidth: 'auto',
    px: { xs: 1.5, sm: 2.5 },
    py: 1,
    textTransform: 'none',
    fontWeight: 500,
    
    color: '#64748b',
    opacity: 1,
    borderRadius: 0,
    flexShrink: 0,
    transition: 'color 0.2s ease',
    '&.Mui-selected': {
      color: '#1E88E5',
      fontWeight: 600,
      backgroundColor: 'transparent',
    },
    '&:hover': {
      color: '#334155',
      backgroundColor: 'transparent',
    },
  },
};

const getRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.attributes)) return data.attributes;
  if (Array.isArray(data?.model)) return data.model;
  if (Array.isArray(data?.models)) return data.models;
  return [];
};

const valueToText = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ') || '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const getTypeDotColor = (type) => {
  const t = String(type).toLowerCase();
  if (t === 'string' || t === 'varchar') return '#3b82f6';
  if (t === 'int' || t === 'integer' || t === 'number' || t === 'float' || t === 'decimal')
    return '#8b5cf6';
  if (t === 'boolean' || t === 'bool') return '#f59e0b';
  if (t === 'date' || t === 'timestamp' || t === 'datetime') return '#ec4899';
  return '#94a3b8';
};

const renderTypeChip = (type) => {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.25,
        py: 0.25,
        borderRadius: '6px',
        
        fontWeight: 600,
        backgroundColor: '#f8fafc',
        color: '#475569',
        border: '1px solid #e2e8f0',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {type}
    </Box>
  );
};

const renderBooleanChip = (value) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1.25,
      py: 0.25,
      borderRadius: '6px',
      
      fontWeight: 600,
      backgroundColor: value ? '#f0fdf4' : '#f8fafc',
      color: value ? '#15803d' : '#64748b',
      border: '1px solid',
      borderColor: value ? '#bbf7d0' : '#e2e8f0',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
    }}
  >
    {value ? (
      <CheckIcon sx={{ fontSize: 14 }} />
    ) : (
      <RemoveIcon sx={{ fontSize: 14 }} />
    )}
    {value ? 'Yes' : 'No'}
  </Box>
);

const renderTruncatedCell = (value, align = 'left', wrap = false) => (
  <Tooltip title={String(value)}>
    <Box
      component="span"
      sx={{
        display: 'block',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        overflow: wrap ? 'visible' : 'hidden',
        textOverflow: wrap ? 'clip' : 'ellipsis',
        whiteSpace: wrap ? 'normal' : 'nowrap',
        wordBreak: wrap ? 'break-word' : 'normal',
        overflowWrap: wrap ? 'break-word' : 'normal',
        textAlign: align,
        
        color: '#475569',
        textAlignLast: align,
      }}
    >
      {value}
    </Box>
  </Tooltip>
);

const ConfigCrudPage = ({
  title,
  storageKey,
  storageVersion,
  columns,
  fields,
  seedRows,
  apiConfig,
  initialPayload,
  getRowKey,
  addButtonLabel,
  onAdd: onAddProp,
  onEdit: onEditProp,
}) => {
  const { loading, error, execute } = useAPICall();
  const configTabs = useConfigTabs();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [localError, setLocalError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, target: null });
  void fields;

  const versionKey = `${storageKey}__version`;

  const persistRows = useCallback(
    (nextRows) => {
      localStorage.setItem(storageKey, JSON.stringify(nextRows));
      localStorage.setItem(versionKey, storageVersion);
      setRows(nextRows);
    },
    [storageKey, storageVersion, versionKey]
  );

  const loadRows = useCallback(async () => {
    setLocalError('');
    const storedVersion = localStorage.getItem(versionKey);

    if (storedVersion && storedVersion !== storageVersion) {
      localStorage.removeItem(storageKey);
    }

    const cached = localStorage.getItem(storageKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setRows(parsed);
          return;
        }
      } catch (err) {
        void err;
      }
    }

    if (Array.isArray(seedRows) && seedRows.length > 0) {
      persistRows(seedRows);
      return;
    }

    if (!apiConfig) {
      setRows([]);
      return;
    }

    try {
      const response = await execute(apiConfig, initialPayload, {
        errorMessage: `Unable to load ${title.toLowerCase()}.`,
        showErrorToast: false,
        retries: 1,
      });
      const nextRows = getRows(response);
      persistRows(nextRows);
    } catch (err) {
      void err;
      setLocalError(`Unable to load ${title.toLowerCase()}.`);
    }
  }, [
    apiConfig,
    execute,
    initialPayload,
    persistRows,
    seedRows,
    storageKey,
    storageVersion,
    title,
    versionKey,
  ]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const onAdd = () => {
    if (onAddProp) {
      onAddProp();
      return;
    }
    navigate(`${location.pathname}/workspace/new`);
  };

  const onEdit = useCallback(
    (row) => {
      if (onEditProp) {
        onEditProp(row);
        return;
      }
      const key = String(getRowKey(row));
      navigate(`${location.pathname}/workspace/edit/${encodeURIComponent(key)}`, {
        state: { row },
      });
    },
    [getRowKey, location.pathname, navigate, onEditProp]
  );

  const onDeleteRequest = useCallback((row) => {
    setDeleteConfirm({ open: true, target: row });
  }, []);

  const confirmDelete = useCallback(() => {
    if (!deleteConfirm.target) return;

    const key = String(getRowKey(deleteConfirm.target));
    const nextRows = rows.filter((item) => String(getRowKey(item)) !== key);
    persistRows(nextRows);
    setDeleteConfirm({ open: false, target: null });
  }, [deleteConfirm.target, getRowKey, persistRows, rows]);

  const closeDeleteDialog = useCallback(() => {
    setDeleteConfirm({ open: false, target: null });
  }, []);

  const deleteItemName = useMemo(() => {
    const normalizedTitle = title?.replace(/s$/, '').toLowerCase() || 'item';
    return normalizedTitle;
  }, [title]);

  const tableColumns = useMemo(() => {
    const baseColumns = columns.map((column) => {
      const colId = column.id || column.label;
      const isBoolean =
        colId.toLowerCase() === 'required' ||
        colId.toLowerCase() === 'searchable' ||
        colId.toLowerCase() === 'active' ||
        colId.toLowerCase() === 'status';
      const isType =
        colId.toLowerCase() === 'type' ||
        colId.toLowerCase() === 'datatype' ||
        colId.toLowerCase() === 'data_type';
      const isName = colId.toLowerCase() === 'name' || colId.toLowerCase() === 'field_name';
      const formatLabel = (value) => {
        if (!value) return '';

        return value
          .replace(/_/g, ' ')              // Replace underscores with spaces
          .replace(/\b\w/g, (c) => c.toUpperCase()); // Capitalize each word
      };
      return {
        header: column.label,
        id: colId,
        size: column.size ?? DEFAULT_CONFIG_COLUMN_WIDTH,
        minSize: column.minSize ?? column.minWidth ?? column.size ?? DEFAULT_CONFIG_COLUMN_WIDTH,
        maxSize: column.maxSize ?? column.maxWidth ?? column.size ?? DEFAULT_CONFIG_COLUMN_WIDTH,
        accessorFn: (row) => {
          if (typeof column.render === 'function') {
            const rendered = column.render(row);
            if (typeof rendered !== 'object') return rendered;
          }
          return column.keys
            .map((key) => row?.[key])
            .find((item) => item !== undefined && item !== null && item !== '');
        },
        Cell: ({ cell, row }) => {
          const value = cell.getValue();

          if (typeof column.render === 'function') {
            const custom = column.render(row.original);
            if (typeof custom === 'object') return custom;
          }

          return renderTruncatedCell(valueToText(value), column.align || 'left', column.wrap);
        },
      };
    });

    return [
      ...baseColumns,
      {
        header: 'Action',
        id: 'actions',
        size: 110,
        minSize: 110,
        maxSize: 110,
        enableSorting: false,
        muiTableHeadCellProps: { align: 'center' },
        muiTableBodyCellProps: { align: 'center' },

        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Tooltip title="Edit">
              {/* eslint-disable-next-line react/prop-types */}
              <IconButton size="small" color="primary" onClick={() => onEdit(row.original)} sx={{ '&:hover': { backgroundColor: alpha('#1E88E5', 0.1) } }}>
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              {/* eslint-disable-next-line react/prop-types */}
              <IconButton size="small" color="error" onClick={() => onDeleteRequest(row.original)} sx={{ '&:hover': { backgroundColor: alpha('#d32f2f', 0.1) } }}>
                <DeleteRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ];
  }, [columns, onDeleteRequest, onEdit]);

  return (
    <Box sx={{ width: '100%' }}>
      {configTabs && (
        <Box sx={{ mb: 2, borderBottom: '1px solid #e2e8f0' }}>
          <Tabs
            value={configTabs.activeTab}
            onChange={configTabs.onTabChange}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{
              style: {
                backgroundColor: '#1E88E5',
                height: '3px',
                borderRadius: '3px',
              }
            }}
            sx={configTabsSx}
          >
            {configTabs.tabItems.map((item) => (
              <Tab
                key={item.id}
                value={item.id}
                label={item.label}
                disableRipple
              />
            ))}
          </Tabs>
        </Box>
      )}
      <Box sx={{ width: '100%' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <GlobalMaterialTable
            columns={tableColumns}
            data={rows}
            isLoading={loading}
            error={error || localError}
            emptyMessage="No records found"
            initialState={{ showGlobalFilter: false, showColumnFilters: false }}
            tableTitle={title}
            tableCount={rows.length}
            renderRightToolbarCustomActions={() => (
              <Button className="action-btn close-btn" 
                variant="contained new-primary-button"
                onClick={onAdd}
                sx={toolbarButtonSx}
                // startIcon={<AddIcon />}
              >
                {addButtonLabel}
              </Button>
            )}
            enablePagination
            enableBottomToolbar
            manualPagination={false}
            page={pagination.pageIndex + 1}
            pageSize={pagination.pageSize}
            onPageChange={(p) => setPagination(prev => ({ ...prev, pageIndex: p - 1 }))}
            onPageSizeChange={(ps) => setPagination(prev => ({ ...prev, pageSize: ps }))}
            getRowId={(row) => String(getRowKey(row))}
          />
        )}
      </Box>

      <ConfirmDeleteDialog
        open={deleteConfirm.open}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        itemName={deleteItemName}
      />
    </Box>
  );
};

ConfigCrudPage.propTypes = {
  title: PropTypes.string.isRequired,
  storageKey: PropTypes.string.isRequired,
  storageVersion: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      keys: PropTypes.arrayOf(PropTypes.string),
      render: PropTypes.func,
    })
  ).isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string,
    })
  ).isRequired,
  seedRows: PropTypes.arrayOf(PropTypes.object),
  apiConfig: PropTypes.shape({
    endpoint: PropTypes.string.isRequired,
    method: PropTypes.string.isRequired,
  }),
  initialPayload: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  getRowKey: PropTypes.func,
  addButtonLabel: PropTypes.string,
};

ConfigCrudPage.defaultProps = {
  storageVersion: 'v1',
  seedRows: [],
  apiConfig: null,
  initialPayload: null,
  getRowKey: (row) => row.name || row.displayName || row.attribute || row.id || JSON.stringify(row),
  addButtonLabel: 'Add',
};

export default ConfigCrudPage;
